// src/components/common/vibe/GiveKudos.js
import React, { useState } from 'react';
import { X, Award, Users, Smile } from 'lucide-react';
import { useEmployees } from '../../../contexts/EmployeeContext';
import vibeService from '../../../services/vibe.service';

const KUDOS_TYPES = [
    { value: 'general', label: 'General Recognition', icon: '⭐', color: 'bg-blue-100 text-blue-800' },
    { value: 'teamwork', label: 'Great Teamwork', icon: '🤝', color: 'bg-purple-100 text-purple-800' },
    { value: 'innovation', label: 'Innovation', icon: '💡', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'leadership', label: 'Leadership', icon: '👑', color: 'bg-red-100 text-red-800' },
    { value: 'helpfulness', label: 'Helpfulness', icon: '🙌', color: 'bg-green-100 text-green-800' }
];

const KUDOS_EMOJIS = ['👏', '🎉', '🌟', '🚀', '💪', '🔥', '✨', '🏆', '💯', '🎯'];

const GiveKudos = ({ isOpen, onClose, onKudosGiven, theme }) => {
    const { employees } = useEmployees();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        given_to: [],
        kudos_message: '',
        kudos_type: 'general',
        selected_emoji: '👏'
    });
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const [employeeSearch, setEmployeeSearch] = useState('');

    // Filter employees for selection
    const filteredEmployees = employees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(employeeSearch.toLowerCase()) &&
        !formData.given_to.includes(emp.id)
    );

    const handleAddEmployee = (employee) => {
        setFormData({
            ...formData,
            given_to: [...formData.given_to, employee.id]
        });
        setEmployeeSearch('');
        setShowEmployeeDropdown(false);
    };

    const handleRemoveEmployee = (employeeId) => {
        setFormData({
            ...formData,
            given_to: formData.given_to.filter(id => id !== employeeId)
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        //console.log('🏆 Giving kudos...', formData);

        // Validation
        if (formData.given_to.length === 0) {
            alert('Please select at least one colleague to recognize.');
            return;
        }

        if (!formData.kudos_message.trim()) {
            alert('Please write a message describing their contribution.');
            return;
        }

        setLoading(true);
        try {
            // Add emoji to message
            const messageWithEmoji = `${formData.selected_emoji} ${formData.kudos_message}`;

            await vibeService.giveKudos({
                given_to: formData.given_to,
                kudos_message: messageWithEmoji,
                kudos_type: formData.kudos_type,
                visibility: 'company'
            });

            //console.log('✅ Kudos given successfully');
            alert('✅ Kudos sent successfully! Your recognition is now visible to the entire company.');

            // Reset form
            setFormData({
                given_to: [],
                kudos_message: '',
                kudos_type: 'general',
                selected_emoji: '👏'
            });

            if (onKudosGiven) {
                await onKudosGiven();
            }

            onClose();
        } catch (error) {
            console.error('❌ Error giving kudos:', error);
            alert(`❌ Failed to send kudos: ${error.response?.data?.message || error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl p-6 m-4 ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                            <Award className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div>
                            <h2 className={`text-2xl font-bold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Give Kudos
                            </h2>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Recognize your team members' great work
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg ${
                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Recognize (To field) */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            <Users className="w-4 h-4 inline mr-1" />
                            Recognize <span className="text-red-500">*</span>
                        </label>

                        {/* Selected Employees */}
                        {formData.given_to.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {formData.given_to.map(empId => {
                                    const emp = employees.find(e => e.id === empId);
                                    if (!emp) return null;
                                    return (
                                        <span
                                            key={empId}
                                            className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${
                                                theme === 'dark'
                                                    ? 'bg-blue-900/30 text-blue-400'
                                                    : 'bg-blue-100 text-blue-700'
                                            }`}
                                        >
                                            {emp.first_name} {emp.last_name}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveEmployee(empId)}
                                                className="hover:bg-black/10 dark:hover:bg-white/10 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Search Input */}
                        <div className="relative">
                            <input
                                type="text"
                                value={employeeSearch}
                                onChange={(e) => {
                                    setEmployeeSearch(e.target.value);
                                    setShowEmployeeDropdown(true);
                                }}
                                onFocus={() => setShowEmployeeDropdown(true)}
                                placeholder="Search and select colleagues..."
                                className={`w-full px-4 py-2 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                }`}
                            />

                            {/* Employee Dropdown */}
                            {showEmployeeDropdown && employeeSearch && filteredEmployees.length > 0 && (
                                <div className={`absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border shadow-lg ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600'
                                        : 'bg-white border-gray-200'
                                }`}>
                                    {filteredEmployees.slice(0, 10).map((emp) => (
                                        <button
                                            key={emp.id}
                                            type="button"
                                            onClick={() => handleAddEmployee(emp)}
                                            className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}
                                        >
                                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                                {emp.first_name[0]}{emp.last_name[0]}
                                            </div>
                                            <div>
                                                <div className="font-medium">{emp.first_name} {emp.last_name}</div>
                                                <div className="text-xs opacity-75">{emp.designation}</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Kudos Type */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Recognition Type
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {KUDOS_TYPES.map((type) => (
                                <button
                                    key={type.value}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, kudos_type: type.value })}
                                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                                        formData.kudos_type === type.value
                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                            : theme === 'dark'
                                                ? 'border-gray-700 hover:border-gray-600'
                                                : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="text-2xl mb-1">{type.icon}</div>
                                    <div className={`text-xs font-medium ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        {type.label}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Emoji Selection */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            <Smile className="w-4 h-4 inline mr-1" />
                            Add Emoji
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {KUDOS_EMOJIS.map((emoji) => (
                                <button
                                    key={emoji}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, selected_emoji: emoji })}
                                    className={`w-12 h-12 text-2xl rounded-lg transition-all ${
                                        formData.selected_emoji === emoji
                                            ? 'bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-600'
                                            : theme === 'dark'
                                                ? 'bg-gray-700 hover:bg-gray-600'
                                                : 'bg-gray-100 hover:bg-gray-200'
                                    }`}
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Message */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Your Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={formData.kudos_message}
                            onChange={(e) => setFormData({ ...formData, kudos_message: e.target.value })}
                            placeholder="Describe their contribution and why you're recognizing them..."
                            rows={4}
                            maxLength={500}
                            className={`w-full px-4 py-3 rounded-lg border resize-none ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            }`}
                        />
                        <div className={`text-xs text-right mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                            {formData.kudos_message.length}/500 characters
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`flex-1 px-6 py-3 rounded-lg font-medium ${
                                theme === 'dark'
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex-1 px-6 py-3 rounded-lg font-medium text-white ${
                                loading
                                    ? 'bg-yellow-400 cursor-not-allowed'
                                    : 'bg-yellow-600 hover:bg-yellow-700'
                            }`}
                        >
                            {loading ? 'Sending...' : '🏆 Send Kudos'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GiveKudos;
